import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { chevronDownOutline, chevronForwardOutline } from 'ionicons/icons';
import IonIcon from '../components/IonIcon';
import { isStepResolved } from '../runEvidence';
import type { WebRun, WebRunStep } from '../runTypes';
import RunListToolbar from './RunListToolbar';
import RunStepRow from './RunStepRow';

interface Props {
  run: WebRun;
  pendingStepId?: string | null;
  canOpenSteps: boolean;
  /** Defaults to `canOpenSteps`. Owners reviewing a delegated run can open steps but not swipe. */
  canSwipe?: boolean;
  onOpenStep?: (step: WebRunStep) => void;
  onSwipeYes?: (step: WebRunStep) => void;
  onSwipeNo?: (step: WebRunStep) => void;
  onSwipeReopen?: (step: WebRunStep) => void;
  children?: ReactNode;
  toolbarClassName?: string;
}

export default function RunListView({
  run,
  pendingStepId,
  canOpenSteps,
  canSwipe = canOpenSteps,
  onOpenStep,
  onSwipeYes,
  onSwipeNo,
  onSwipeReopen,
  children,
  toolbarClassName,
}: Props) {
  const sections = useMemo(() => {
    const snapshot = [...(run.sectionsSnapshot || [])].sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0),
    );
    const steps = [...run.steps].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    if (snapshot.length === 0) {
      return [{ sectionId: '_all', name: run.bucketName, steps }];
    }
    const grouped = snapshot.map((section) => ({
      sectionId: section.sectionId,
      name: section.name || 'Section',
      steps: steps.filter((s) => s.sectionId === section.sectionId),
    })).filter((s) => s.steps.length > 0);
    const known = new Set(snapshot.map((s) => s.sectionId));
    const orphans = steps.filter((s) => !s.sectionId || !known.has(s.sectionId));
    if (orphans.length > 0) {
      grouped.push({ sectionId: '_other', name: run.bucketName, steps: orphans });
    }
    return grouped;
  }, [run]);

  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [pendingJumpId, setPendingJumpId] = useState<string | null>(null);

  const nextOpen = useMemo(
    () => sections.flatMap((s) => s.steps).find((step) => !isStepResolved(step)) || null,
    [sections],
  );

  useEffect(() => {
    if (!pendingJumpId) return;
    const el = document.querySelector(`[data-run-step="${pendingJumpId}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setPendingJumpId(null);
  }, [pendingJumpId, collapsed]);

  const jumpToNext = () => {
    if (!nextOpen) return;
    const sectionId = sections.find((s) => s.steps.some((step) => step.stepId === nextOpen.stepId))
      ?.sectionId;
    if (sectionId) {
      setCollapsed((prev) => ({ ...prev, [sectionId]: false }));
    }
    setPendingJumpId(nextOpen.stepId);
  };

  const expandAll = () => {
    setCollapsed({});
  };

  const collapseAll = () => {
    const next: Record<string, boolean> = {};
    for (const section of sections) next[section.sectionId] = true;
    setCollapsed(next);
  };

  return (
    <>
      <RunListToolbar
        hasNext={!!nextOpen}
        onNext={jumpToNext}
        onExpandAll={expandAll}
        onCollapseAll={collapseAll}
        className={toolbarClassName}
      />
      <div className="space-y-3">
      {sections.map((section) => {
        const closed = collapsed[section.sectionId] === true;
        const openCount = section.steps.filter((s) => !isStepResolved(s)).length;
        return (
          <section key={section.sectionId} className="overflow-hidden rounded-2xl border border-x9-line bg-white">
            <button
              type="button"
              className="flex w-full items-center gap-2 px-4 py-3 text-left"
              onClick={() => setCollapsed((prev) => ({
                ...prev,
                [section.sectionId]: !closed,
              }))}
              aria-expanded={!closed}
            >
              <IonIcon
                icon={closed ? chevronForwardOutline : chevronDownOutline}
                className="h-4 w-4 text-x9-marineSoft"
              />
              <span className="flex-1 text-sm font-semibold text-x9-ink">
                {section.name}
              </span>
              <span className="text-xs text-x9-marineSoft">
                {openCount}/{section.steps.length} open
              </span>
            </button>
            {!closed && (
              <ul className="divide-y divide-x9-line border-t border-x9-line">
                {section.steps.map((step) => (
                  <li key={step.stepId} data-run-step={step.stepId}>
                    <RunStepRow
                      step={step}
                      pending={pendingStepId === step.stepId}
                      canSwipe={canSwipe}
                      onOpen={canOpenSteps ? onOpenStep : undefined}
                      onYes={onSwipeYes}
                      onNo={onSwipeNo}
                      onReopen={onSwipeReopen}
                    />
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}
      </div>
      {children}
    </>
  );
}
